import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { withLoadResilience } from './loadResilience';
import type { RestaurantIdentity } from '@/types';
import { coercePausedChannels } from '@/features/restaurant/pausedChannels';
import {
  DAY_KEYS,
  emptyWeeklyHours,
  normalizeFirstSlot,
  normalizePreorderMaxDays,
  normalizeReservationMaxPartySize,
  normalizeRestaurantType,
  type Closure,
  type WeeklyHours,
} from '@/types/restaurant';

interface RawIdentity {
  uuid: string;
  store_name: string;
  restaurant_type?: string;
  logo: string | null;
  logo_inverse?: string | null;
  favicon?: string | null;
  favicon_inverse?: string | null;
  address: string | null;
  postal_code?: string | null;
  city?: string | null;
  phone: string | null;
  country?: string;
  timezone?: string | null;
  theme: RestaurantIdentity['theme'];
  hours: unknown;
  closures?: unknown;
  reservation_closures?: unknown;
  /** SC-1738 — resolved by the hub (= `hours` when the restaurant set none). */
  reservation_hours?: unknown;
  google_review_link: string | null;
  instagram_link: string | null;
  tiktok_link: string | null;
  online_ordering_enabled?: boolean;
  online_ordering_paused_channels?: unknown;
  online_ordering_feature_enabled?: boolean;
  preorder_max_days?: number;
  reservation_max_party_size?: number;
  first_slot_emporter?: unknown;
  first_slot_livraison?: unknown;
  asap_emporter?: boolean;
  asap_livraison?: boolean;
}

function coerceClosures(raw: unknown): Closure[] {
  if (!Array.isArray(raw)) return [];
  const out: Closure[] = [];
  for (const item of raw) {
    if (item === null || typeof item !== 'object') continue;
    const c = item as Record<string, unknown>;
    if (typeof c.from !== 'string' || typeof c.to !== 'string') continue;
    const entry: Closure = { from: c.from, to: c.to };
    if (typeof c.label === 'string') entry.label = c.label;
    out.push(entry);
  }
  return out;
}

function coerceHours(raw: unknown): WeeklyHours {
  if (raw === null || raw === undefined) return emptyWeeklyHours();
  if (typeof raw !== 'object' || Array.isArray(raw)) return emptyWeeklyHours();
  const src = raw as Record<string, unknown>;
  const out = emptyWeeklyHours();
  for (const day of DAY_KEYS) {
    const v = src[day];
    if (Array.isArray(v)) out[day] = v as WeeklyHours[typeof day];
  }
  return out;
}

function transformIdentity(raw: RawIdentity): RestaurantIdentity {
  return {
    uuid: raw.uuid,
    name: raw.store_name,
    restaurantType: normalizeRestaurantType(raw.restaurant_type),
    logoUrl: raw.logo,
    logoInverseUrl: raw.logo_inverse ?? null,
    faviconUrl: raw.favicon ?? null,
    faviconInverseUrl: raw.favicon_inverse ?? null,
    address: raw.address,
    postalCode: raw.postal_code ?? null,
    city: raw.city ?? null,
    phone: raw.phone,
    country: raw.country ?? 'FR',
    timezone: raw.timezone ?? null,
    theme: raw.theme,
    hours: coerceHours(raw.hours),
    closures: coerceClosures(raw.closures),
    reservationClosures: coerceClosures(raw.reservation_closures),
    reservationHours:
      raw.reservation_hours === undefined ? undefined : coerceHours(raw.reservation_hours),
    googleReviewLink: raw.google_review_link,
    instagramLink: raw.instagram_link,
    tiktokLink: raw.tiktok_link,
    onlineOrderingEnabled: raw.online_ordering_enabled ?? true,
    onlineOrderingPausedChannels: coercePausedChannels(raw.online_ordering_paused_channels),
    onlineOrderingFeatureEnabled: raw.online_ordering_feature_enabled ?? true,
    preorderMaxDays: normalizePreorderMaxDays(raw.preorder_max_days),
    reservationMaxPartySize: normalizeReservationMaxPartySize(raw.reservation_max_party_size),
    firstSlotEmporter: normalizeFirstSlot(raw.first_slot_emporter),
    firstSlotLivraison: normalizeFirstSlot(raw.first_slot_livraison),
    asapEmporter: raw.asap_emporter === true,
    asapLivraison: raw.asap_livraison === true,
  };
}

export const hubApi = createApi({
  reducerPath: 'hubApi',
  baseQuery: withLoadResilience(
    fetchBaseQuery({
      baseUrl: import.meta.env.VITE_HUB_URL,
    }),
  ),
  tagTypes: ['Restaurant'],
  endpoints: (build) => ({
    getRestaurantIdentity: build.query<RestaurantIdentity, string>({
      query: (uuid) => `/restaurants/public/${uuid}/`,
      transformResponse: transformIdentity,
      providesTags: (_r, _e, uuid) => [{ type: 'Restaurant', id: uuid }],
    }),
  }),
});

export const { useGetRestaurantIdentityQuery } = hubApi;
